/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useEffect, type MutableRefObject } from "react";

import type { FilterRecord, UrlSyncState } from "./types";

type SetUrlState = (next: Partial<UrlSyncState>) => void;

export function useStandardFilterUrlSyncEffect<TFilter extends FilterRecord>(
  args: {
    shouldSyncWithUrl: boolean;
    hasMountedRef: MutableRefObject<boolean>;
    previousUrlStateRef: MutableRefObject<Partial<UrlSyncState>>;
    searchTermServer: string;
    page: number;
    pageSizeState: number;
    pageSize: number;
    setUrlState: SetUrlState;
    searchKey: string;
    pageKey: string;
    pageSizeKey: string;
    filterUrlState: Partial<UrlSyncState>;
    filterKeys: (keyof TFilter & string)[];
    filterUrlKeyMap: Record<string, string>;
  },
): void {
  const {
    shouldSyncWithUrl,
    hasMountedRef,
    previousUrlStateRef,
    searchTermServer,
    page,
    pageSizeState,
    pageSize,
    setUrlState,
    searchKey,
    pageKey,
    pageSizeKey,
    filterUrlState,
    filterKeys,
    filterUrlKeyMap,
  } = args;

  useEffect(() => {
    if (!shouldSyncWithUrl || !hasMountedRef.current) {
      return;
    }

    const newUrlState: Partial<UrlSyncState> = {
      [searchKey]: searchTermServer || undefined,
      [pageKey]: page > 1 ? page : undefined,
      [pageSizeKey]: pageSizeState !== pageSize ? pageSizeState : undefined,
      ...filterUrlState,
    };

    const prev = previousUrlStateRef.current;
    let hasChanges = false;

    const newSearch = newUrlState[searchKey] ?? "";
    const prevSearch = prev[searchKey] ?? "";
    if (String(newSearch) !== String(prevSearch)) {
      hasChanges = true;
    }

    const newPage = Number(newUrlState[pageKey] ?? 1);
    const prevPage = Number(prev[pageKey] ?? 1);
    if (newPage !== prevPage) {
      hasChanges = true;
    }

    const newPageSize = Number(newUrlState[pageSizeKey] ?? pageSize);
    const prevPageSize = Number(prev[pageSizeKey] ?? pageSize);
    if (newPageSize !== prevPageSize) {
      hasChanges = true;
    }

    if (!hasChanges) {
      for (const key of filterKeys) {
        const urlKey = filterUrlKeyMap[key];
        if (!urlKey) continue;
        const newValue = newUrlState[urlKey];
        const prevValue = prev[urlKey];
        if (newValue !== prevValue) {
          hasChanges = true;
          break;
        }
      }
    }

    if (hasChanges) {
      previousUrlStateRef.current = { ...newUrlState };
      setUrlState(newUrlState);
    }
  }, [
    shouldSyncWithUrl,
    hasMountedRef,
    previousUrlStateRef,
    searchTermServer,
    page,
    pageSizeState,
    setUrlState,
    searchKey,
    pageKey,
    pageSizeKey,
    filterUrlState,
    filterKeys,
    filterUrlKeyMap,
    pageSize,
  ]);
}
